import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  CalendarBlank,
  MapPin,
  CurrencyDollar,
  Storefront,
} from "@phosphor-icons/react";
import { format, subDays } from "date-fns";

export default function AdminReportsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [reportData, setReportData] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchReport(token);
  }, [navigate]);

  const fetchReport = async (token) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activity/range`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem("adminToken")}` },
        params: {
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd")
        }
      });
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    fetchReport();
  };

  // Calculate totals
  const totals = reportData?.salespeople?.reduce((acc, person) => ({
    visits: acc.visits + person.visits,
    sales: acc.sales + person.sales_total,
    customers: acc.customers + person.customers_added
  }), { visits: 0, sales: 0, customers: 0 }) || { visits: 0, sales: 0, customers: 0 };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-reports-page">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 -ml-2 text-[#9CA3AF] hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <p className="text-[#9CA3AF] text-sm">Admin</p>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Date Reports
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Date Range Picker */}
        <div className="card-flat p-4">
          <p className="font-medium text-[#0A0A0A] mb-3">Select Date Range</p>
          <div className="flex gap-3">
            <div className="flex-1">
              <p className="text-sm text-[#4B5563] mb-1">From</p>
              <Popover open={startOpen} onOpenChange={setStartOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="start-date-picker">
                    <CalendarBlank size={18} className="mr-2" />
                    {format(startDate, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      if (date) setStartDate(date);
                      setStartOpen(false);
                    }}
                    disabled={(date) => date > endDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex-1">
              <p className="text-sm text-[#4B5563] mb-1">To</p>
              <Popover open={endOpen} onOpenChange={setEndOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="end-date-picker">
                    <CalendarBlank size={18} className="mr-2" />
                    {format(endDate, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      if (date) setEndDate(date);
                      setEndOpen(false);
                    }}
                    disabled={(date) => date < startDate || date > new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Button
            onClick={handleGenerateReport}
            disabled={loading}
            className="w-full mt-3 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white"
            data-testid="generate-report-button"
          >
            {loading ? "Loading..." : "Generate Report"}
          </Button>
        </div>

        {/* Summary Stats */}
        {reportData && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="stat-card text-center">
                <MapPin size={20} className="mx-auto text-[#10B981] mb-1" weight="fill" />
                <p className="stat-value text-xl">{totals.visits}</p>
                <p className="stat-label text-xs">Total Visits</p>
              </div>
              <div className="stat-card text-center">
                <CurrencyDollar size={20} className="mx-auto text-[#F59E0B] mb-1" weight="fill" />
                <p className="stat-value text-xl">${totals.sales.toLocaleString()}</p>
                <p className="stat-label text-xs">Total Sales</p>
              </div>
              <div className="stat-card text-center">
                <Storefront size={20} className="mx-auto text-[#0055FF] mb-1" weight="fill" />
                <p className="stat-value text-xl">{totals.customers}</p>
                <p className="stat-label text-xs">New Customers</p>
              </div>
            </div>

            {/* Salespeople Breakdown */}
            <div>
              <h2 className="font-bold text-[#0A0A0A] mb-3" style={{ fontFamily: "'Chivo', sans-serif" }}>
                Salesperson Breakdown
              </h2>
              <div className="card-flat overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-5 gap-2 p-3 bg-[#F3F4F6] text-xs font-medium text-[#4B5563]">
                  <div className="col-span-2">Name</div>
                  <div className="text-center">Visits</div>
                  <div className="text-center">Sales</div>
                  <div className="text-center">Customers</div>
                </div>
                {reportData.salespeople.map((person, index) => (
                  <div
                    key={person.user_id}
                    className="grid grid-cols-5 gap-2 p-3 border-t border-[#E5E7EB] items-center cursor-pointer hover:bg-[#F9FAFB]"
                    onClick={() => navigate(`/admin/salesperson/${person.user_id}`)}
                    data-testid={`report-row-${index}`}
                  >
                    <div className="col-span-2">
                      <p className="font-medium text-[#0A0A0A] text-sm">{person.user_name}</p>
                    </div>
                    <div className="text-center font-medium">{person.visits}</div>
                    <div className="text-center font-medium text-[#10B981]">${person.sales_total}</div>
                    <div className="text-center font-medium">{person.customers_added}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
